import React from "react";
import { UserCheck, Eye, Award, Video, MapPin, Star, Calendar } from "lucide-react";

const TIER_CHIP = {
  ouro:   "bg-gradient-to-r from-[#F1C232] to-[#D4A017] text-[#3B2F0B]",
  prata:  "bg-gradient-to-r from-slate-300 to-slate-500 text-slate-900",
  bronze: "bg-gradient-to-r from-[#CD7F32] to-[#8B4513] text-white",
};

const isCertActive = (p) => {
  if (!p?.certification_tier) return false;
  if (!["paid", "waived"].includes((p.certification_payment_status || "").toLowerCase())) return false;
  if (!p.certification_expires_at) return false;
  return new Date(p.certification_expires_at) > new Date();
};

const money = (v) => (v == null ? "—" : Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" }));

export const CaseProfessionalsList = ({ professionals, onIndicate, onViewDetails, onBook }) => {
  const [onlyCertified, setOnlyCertified] = React.useState(false);
  const certifiedCount = (professionals || []).filter(isCertActive).length;
  const visible = onlyCertified
    ? (professionals || []).filter(isCertActive)
    : (professionals || []);

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-6" data-testid="case-professionals-list">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-display font-bold text-xl text-[#0B2B62] flex items-center gap-2">
          <UserCheck className="w-5 h-5" /> Profissionais compatíveis
        </h2>
        <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none"
          data-testid="profs-only-certified-toggle">
          <input type="checkbox" checked={onlyCertified} onChange={(e) => setOnlyCertified(e.target.checked)}
            className="accent-amber-600" />
          <Award className="w-3 h-3 text-amber-600" />
          Só certificados ({certifiedCount}/{(professionals || []).length})
        </label>
      </div>

      {visible.length === 0 && (
        <div className="text-sm text-slate-500 italic py-6 text-center">
          Nenhum profissional aprovado nesta base ainda. Aguarde novos cadastros ou ajuste os filtros.
        </div>
      )}

      <div className="space-y-3">
        {visible.map((p) => {
          const name = p.display_name || p.name || "Profissional";
          const council = [p.council_type, p.council_number, p.council_state].filter(Boolean).join(" ");
          const cats = (p.categories || []).slice(0, 3).join(", ");
          const mods = (p.modality || []).map((m) => String(m).toLowerCase());
          const isOnline = mods.some((m) => m.includes("online") || m.includes("tele"));
          return (
            <div key={p.id} data-testid={`prof-row-${p.id}`}
              className="p-4 border border-slate-200 rounded-lg flex items-start justify-between gap-3 hover:border-[#0B2B62] transition-colors">
              <div className="flex items-start gap-3 min-w-0">
                {p.photo_url ? (
                  <img src={p.photo_url} alt={name} loading="lazy"
                    className="w-12 h-12 rounded-full object-cover border border-slate-200 shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500 font-bold text-sm shrink-0">
                    {name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display font-semibold text-[#0B2B62] truncate">{name}</h3>
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-mono">
                      match {p.match_score}%
                    </span>
                    {isCertActive(p) && (
                      <span className={`text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-widest inline-flex items-center gap-0.5 ${TIER_CHIP[p.certification_tier] || ""}`}>
                        <Award className="w-2.5 h-2.5" /> Certificado · {p.certification_tier}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    {council && <span>{council}</span>}
                    {cats && <span>· {cats}</span>}
                    {p.city && <span className="inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" />{p.city}{p.state ? `, ${p.state}` : ""}</span>}
                    {isOnline && <span className="inline-flex items-center gap-0.5 text-emerald-700"><Video className="w-3 h-3" /> Online</span>}
                    {p.session_value != null && <span>· {money(p.session_value)}/sessão</span>}
                  </div>
                  {p.match_reasons && p.match_reasons.length > 0 && (
                    <div className="mt-1.5 text-xs text-slate-600">
                      {p.match_reasons.join(" · ")}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                {onViewDetails && (
                  <button onClick={() => onViewDetails(p.id)} data-testid={`prof-details-${p.id}`}
                    className="inline-flex items-center justify-center gap-1 px-3 py-1.5 border border-slate-300 text-slate-700 rounded-md text-sm hover:bg-slate-50 transition-colors">
                    <Eye className="w-3.5 h-3.5" /> Ver detalhes
                  </button>
                )}
                <button onClick={() => onIndicate(p)} data-testid={`prof-indicate-${p.id}`}
                  className="inline-flex items-center justify-center gap-1 px-3 py-1.5 border border-[#0B2B62] text-[#0B2B62] rounded-md text-sm hover:bg-slate-50 transition-colors">
                  <Star className="w-3.5 h-3.5" /> Indicar à família
                </button>
                {onBook && (
                  <button onClick={() => onBook(p)} data-testid={`prof-book-${p.id}`}
                    className="inline-flex items-center justify-center gap-1 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-md text-sm font-semibold transition-colors">
                    <Calendar className="w-3.5 h-3.5" /> Reservar sessão
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
